// @ts-nocheck
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import {
  RemoveCircleOutlined as DeleteIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';

import {
  FHSelect,
  FHInputField,
  FHButton,
  FTypography,
  FHAutocomplete,
  FSwitch,
} from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import {
  cleanNumericInputValue,
  getAllInvalidFieldNamesAuto,
  phoneNumberFormatter,
  validateSchema,
  validateSync,
} from '../../../../helpers';
import { FORTA_BLACK, FORTA_ERROR, FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { BackgroundTypes, Themes, usStates } from '../../../../constants';
import { ParentCaregiverRoles, ParentMaritalStatus } from './constants';
import {
  EMAIL_TEST_REGEX,
  getParentCaregiverSchema,
  ParentCaregiverListSchema,
} from './validationSchema';

import type { IParentCaregiver, SelectValueType } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';

function ParentsCaregivers() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);
  const { clientFamilyBackground, parentsCaregivers } = useSelector(
    (state: any) => state.clientFamilyBackgrounds,
  );
  const [list, setList] = useState<IParentCaregiver[]>([]);
  const [errors, setErrors] = useState<{ [key: number]: { [key: string]: boolean } }>({});
  const [firstSavedParent, setFirstSavedParent] = useState<IParentCaregiver | null>(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [savedParentCaregiverCount, setSavedParentCaregiverCount] = useState<number>(0);
  const [fillAddressFromFirstParent, setFillAddressFromFirstParent] = useState<boolean>(false);

  const listOfValidationKeys = [
    'role',
    'name',
    'parent_marital_status',
    'parent_mental_health_history',
  ];

  useEffect(() => {
    if (!fillAddressFromFirstParent || !isAdding || !firstSavedParent) return;
    let unSavedPCIndex: number | null = null;
    let unSavedPCData: IParentCaregiver | null = null;
    const updatedParentCaregiver = parentsCaregivers.map((pc: IParentCaregiver, i: number) => {
      if (pc.id) return pc;

      unSavedPCIndex = i;

      unSavedPCData = {
        ...pc,
        street_address_line_one: firstSavedParent.street_address_line_one,
        street_address_line_two: firstSavedParent.street_address_line_two,
        street_address_city: firstSavedParent.street_address_city,
        street_address_state: firstSavedParent.street_address_state?.toUpperCase(),
        street_address_zip: firstSavedParent.street_address_zip,
      };
      return unSavedPCData;
    });
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'parentsCaregivers',
        data: updatedParentCaregiver,
      }),
    );
    if (unSavedPCIndex !== null && unSavedPCData) {
      handleSave(unSavedPCIndex, unSavedPCData);
    }
  }, [fillAddressFromFirstParent]);

  useEffect(() => {
    setErrors({});
    if (list.length === 0) {
      setErrors({ 0: { 0: true } });
    }
    if (list.length > 0) {
      const allValidationErrors: { [key: number]: { [key: string]: boolean } } = {};
      list.forEach((parentCaregiver: IParentCaregiver, index: number) => {
        const validationErrors = getAllInvalidFieldNamesAuto(
          getParentCaregiverSchema(
            (!firstSavedParent && !parentCaregiver.id) ||
              Boolean(
                firstSavedParent &&
                firstSavedParent.id === parentCaregiver.id &&
                !EMAIL_TEST_REGEX.test(firstSavedParent.email ?? ''),
              ),
          ),
          parentCaregiver,
        );
        allValidationErrors[index] = validationErrors;
      });
      setErrors((prev: any) => ({
        ...prev,
        ...allValidationErrors,
      }));
    }
    list.forEach((parentCaregiver: IParentCaregiver, index: number) => {
      const {
        role: _role,
        parent_marital_status: _parent_marital_status,
        ...fieldsToBeValidated
      } = parentCaregiver;
      //Removing the two fields that are being prefilled
      if (fieldsToBeValidated && Object.keys(fieldsToBeValidated).length !== 0) {
        listOfValidationKeys.forEach((key: string) => {
          handleValidate(index, key, parentCaregiver);
        });
      }
    });

    // Validate the parentandcaregiver list
    ParentCaregiverListSchema.validate(list)
      .then(() => {
        setValidationErrors([]);
      })
      .catch((validationError) => {
        setValidationErrors(validationError.errors);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useEffect(() => {
    if (!parentsCaregivers || !treatmentPlan) return;

    const savedPC = parentsCaregivers.filter((pc: IParentCaregiver) => pc.id !== undefined);

    if (savedPC.length > savedParentCaregiverCount || parentsCaregivers.length === 0) {
      setIsAdding(false);
      setFillAddressFromFirstParent(false);
    }
    setSavedParentCaregiverCount(savedPC.length);

    // Find first parent that's saved in DB
    const firstSavedParent = savedPC.find(
      (pc: IParentCaregiver) =>
        pc.role === ParentCaregiverRoles['Parent and caregiver'] ||
        pc.role === ParentCaregiverRoles['Parent (not a caregiver)'],
    );
    if (firstSavedParent) {
      setFirstSavedParent(JSON.parse(JSON.stringify(firstSavedParent)));
    } else {
      setFirstSavedParent(null);
    }
    setList(parentsCaregivers || []);
  }, [parentsCaregivers, treatmentPlan]);

  useEffect(() => {
    if (!isAdding || firstSavedParent || !selectedClient) return;

    const updatedParentCaregiver = parentsCaregivers.map((pc: IParentCaregiver) => {
      if (pc.id) return pc;
      return {
        ...pc,
        street_address_line_one: pc.street_address_line_one
          ? pc.street_address_line_one
          : selectedClient.street_address_line_one,
        street_address_line_two: pc.street_address_line_two
          ? pc.street_address_line_two
          : selectedClient.street_address_line_two,
        street_address_city: pc.street_address_city
          ? pc.street_address_city
          : selectedClient.street_address_city,
        street_address_state: pc.street_address_state
          ? pc.street_address_state
          : selectedClient.street_address_state?.toUpperCase(),
        street_address_zip: pc.street_address_zip
          ? pc.street_address_zip
          : selectedClient.street_address_zip,
      };
    });
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'parentsCaregivers',
        data: updatedParentCaregiver,
      }),
    );
  }, [firstSavedParent, isAdding]);

  useEffect(() => {
    if (!treatmentPlan) return;

    if (Object.keys(errors).length === 0) {
      dispatch(
        clientFamilyBackgroundActions.updateState({
          key: 'parentCaregiverErrorCounts',
          data: list.length > 0 ? 0 : 1,
        }),
      );
      return;
    }

    let errorCount = 0;
    Object.values(errors).forEach((fieldErrors) => {
      Object.values(fieldErrors).forEach((hasError) => {
        if (hasError) errorCount++;
      });
    });
    errorCount += validationErrors.length;

    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'parentCaregiverErrorCounts',
        data: errorCount,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, list, treatmentPlan]);

  const handleAddNew = async () => {
    if (isAdding) return;
    if (!clientFamilyBackground?.id) {
      const response = await dispatch(
        clientFamilyBackgroundActions.createClientFamilyBackground({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            other_individuals_in_home: '',
          },
          getAccessTokenSilently,
        }),
      );
      if (response.error) {
        return;
      }
    }
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'parentsCaregivers',
        data: [
          ...parentsCaregivers,
          {
            role: ParentCaregiverRoles['Parent and caregiver'],
            parent_marital_status: ParentMaritalStatus['Married'],
          },
        ],
      }),
    );
    setIsAdding(true);
  };

  const handleDelete = async (id?: number) => {
    if (!treatmentPlan || !clientFamilyBackground) return;

    if (id) {
      const res = await dispatch(
        clientFamilyBackgroundActions.removeParentCaregiver({
          treatmentPlanId: treatmentPlan.id,
          backgroundId: clientFamilyBackground.id,
          parentCaregiverId: id,
          getAccessTokenSilently,
        }),
      );
      if (res.error) return;
    } else {
      const filtered = list.filter((pc: IParentCaregiver) => pc.id !== id);
      dispatch(
        clientFamilyBackgroundActions.updateState({
          key: 'parentsCaregivers',
          data: filtered,
        }),
      );
      setIsAdding(false);
    }
  };

  const handleUpdateField = (index: number, key: string, value: any) => {
    let processedValue = value;
    let updatedValues = {};
    if (key === 'street_address_zip') {
      processedValue = cleanNumericInputValue(value.trim());
    }
    if (key === 'phone') {
      processedValue = cleanNumericInputValue(value.trim());
      if (processedValue.length > 10) return;
    }
    if (key === 'role' && value === ParentCaregiverRoles['Caregiver (not parent)']) {
      updatedValues = {
        parent_marital_status: null,
        parent_mental_health_history: null,
        email: null,
        phone: null,
        job: null,
        street_address_line_one: null,
        street_address_line_two: null,
        street_address_city: null,
        street_address_state: null,
        street_address_zip: null,
      };
    }
    const updatedParentCaregiver = parentsCaregivers.map((pc: IParentCaregiver, i: number) =>
      index === i ? { ...pc, [key]: processedValue, ...updatedValues } : pc,
    );
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'parentsCaregivers',
        data: updatedParentCaregiver,
      }),
    );
  };

  const handleSave = async (index: number, data: IParentCaregiver) => {
    if (!treatmentPlan || !clientFamilyBackground || !data) return;

    const isValid = validateSync(
      getParentCaregiverSchema(
        (!firstSavedParent && !data.id) ||
          Boolean(
            firstSavedParent &&
            firstSavedParent.id === data.id &&
            !EMAIL_TEST_REGEX.test(firstSavedParent.email ?? ''),
          ),
      ),
      data,
    );
    let payload: IParentCaregiver = data;
    let trimmedData = { ...data };

    if (data.role === ParentCaregiverRoles['Caregiver (not parent)']) {
      const {
        parent_marital_status: _parent_marital_status,
        parent_mental_health_history: _parent_mental_health_history,
        ...rest
      } = data;
      trimmedData = { ...rest };
    }

    trimmedData = {
      ...trimmedData,
      email: trimmedData.email === '' ? undefined : trimmedData.email,
      phone: trimmedData.phone === '' ? undefined : trimmedData.phone,
      job: trimmedData.job === '' ? undefined : trimmedData.job,
      street_address_line_one:
        trimmedData.street_address_line_one === ''
          ? undefined
          : trimmedData.street_address_line_one,
      street_address_line_two:
        trimmedData.street_address_line_two === ''
          ? undefined
          : trimmedData.street_address_line_two,
      street_address_city:
        trimmedData.street_address_city === '' ? undefined : trimmedData.street_address_city,
      street_address_state:
        trimmedData.street_address_state === '' ? undefined : trimmedData.street_address_state,
      street_address_zip:
        trimmedData.street_address_zip === '' ? undefined : trimmedData.street_address_zip,
      parent_mental_health_history:
        trimmedData.parent_mental_health_history === ''
          ? undefined
          : trimmedData.parent_mental_health_history,
    };

    if (isValid) {
      // Update parent and caregiver background
      if (data.id) {
        const res = await dispatch(
          clientFamilyBackgroundActions.updateParentCaregiver({
            treatmentPlanId: treatmentPlan.id,
            backgroundId: clientFamilyBackground.id,
            data: trimmedData,
            getAccessTokenSilently,
          }),
        );
        if (res.error) return;

        payload = res.payload;
      } else {
        // Create new parent and caregiver background
        const res = await dispatch(
          clientFamilyBackgroundActions.createParentCaregiver({
            treatmentPlanId: treatmentPlan.id,
            backgroundId: clientFamilyBackground.id,
            data: trimmedData,
            index,
            getAccessTokenSilently,
          }),
        );
        if (res.error) return;

        payload = res.payload;
      }
    } else {
      dispatch(
        clientFamilyBackgroundActions.updateState({
          key: 'parentsCaregivers',
          data: parentsCaregivers.map((pc: IParentCaregiver, i: number) =>
            index === i ? payload : pc,
          ),
        }),
      );
    }
  };

  const handleBlur = (index: number, key: string, parentCaregiver: IParentCaregiver) => {
    handleSave(index, parentCaregiver);
  };

  const handleValidate = (index: number, key: string, data: any) => {
    if (!data) return;

    const isValid = validateSchema(
      getParentCaregiverSchema(
        (!firstSavedParent && !data.id) ||
          Boolean(
            firstSavedParent &&
            firstSavedParent.id === data.id &&
            !EMAIL_TEST_REGEX.test(firstSavedParent.email ?? ''),
          ),
      ),
      key,
      data,
    );
    setErrors((prev: any) => ({
      ...prev,
      [index]: prev[index] ? { ...prev[index], [key]: !isValid } : { [key]: !isValid },
    }));
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <FTypography
        fontWeight="bold"
        textAlign={'left'}
        error={validationErrors && validationErrors.length > 0}
      >
        Parents & Caregiver: (Make sure to include all parent and caregiver)
      </FTypography>
      {validationErrors && (
        <Typography textAlign={'left'} color={FORTA_BLUE.light1} mb={1.5}>
          {validationErrors[0]}
        </Typography>
      )}
      {list.map((parentCaregiver: IParentCaregiver, index: number) => (
        <Box key={parentCaregiver.id} data-testid={`${index}-grid`}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              backgroundColor: FORTA_WHITE,
              borderRadius: '10px',
              py: 3,
              pl: isEditable ? 2 : 3,
              pr: 3,
              mt: 3,
            }}
            mb={2}
          >
            {isEditable && (
              <Box width={35}>
                <IconButton sx={{ p: 0, mr: 2 }} onClick={() => handleDelete(parentCaregiver.id)}>
                  <DeleteIcon data-testid="deleteCareGiver" sx={{ color: FORTA_BLUE.primary }} />
                </IconButton>
              </Box>
            )}
            <Grid container spacing={2} textAlign={'left'}>
              <Grid item xs={3}>
                <Typography mb={0.5} fontWeight={700}>
                  Role*
                </Typography>
                <FHSelect
                  data-testid="roleDropDown"
                  inputHeight={40}
                  fullWidth
                  backgroundType={BackgroundTypes.Colored}
                  colorTheme={Themes.Primary}
                  disabled={!isEditable}
                  options={Object.entries(ParentCaregiverRoles).map(([label, value]) => ({
                    value,
                    label,
                  }))}
                  optionDataTestIdPrefix="roleOption"
                  value={parentCaregiver.role}
                  onUpdate={(value: SelectValueType) => {
                    const role = Object.values(ParentCaregiverRoles).find(
                      (option) => option === value,
                    );
                    if (!role) return;
                    let updatedValues = {};
                    if (role === ParentCaregiverRoles['Caregiver (not parent)']) {
                      updatedValues = {
                        parent_marital_status: null,
                        parent_mental_health_history: null,
                        email: null,
                        phone: null,
                        job: null,
                        street_address_line_one: null,
                        street_address_line_two: null,
                        street_address_city: null,
                        street_address_state: null,
                        street_address_zip: null,
                      };
                    }
                    handleUpdateField(index, 'role', role);
                    handleSave(index, {
                      ...parentCaregiver,
                      role,
                      ...updatedValues,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography mb={0.5} fontWeight={700}>
                  Name*
                </Typography>
                <FHInputField
                  data-testid="parentNameTextField"
                  fullWidth
                  coloredBackground
                  colorTheme={Themes.Primary}
                  disabled={!isEditable}
                  inputHeight={40}
                  value={parentCaregiver.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleUpdateField(index, 'name', e.target.value)
                  }
                  onBlur={() => handleBlur(index, 'name', parentCaregiver)}
                  error={errors[index] ? errors[index]['name'] : false}
                />
              </Grid>
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={6}>
                  <Typography mb={0.5} fontWeight={700}>
                    Parent mental health history*
                  </Typography>
                  <FHInputField
                    data-testid="parentMentalHistoryTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.parent_mental_health_history}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'parent_mental_health_history', e.target.value)
                    }
                    onBlur={() =>
                      handleBlur(index, 'parent_mental_health_history', parentCaregiver)
                    }
                    error={errors[index] ? errors[index]['parent_mental_health_history'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={3} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>
                    Marital status*
                  </Typography>
                  <FHSelect
                    data-testid="parentMaritalStatusDropDown"
                    inputHeight={40}
                    fullWidth
                    backgroundType={BackgroundTypes.Colored}
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    options={Object.entries(ParentMaritalStatus).map(([label, value]) => ({
                      value,
                      label,
                    }))}
                    optionDataTestIdPrefix="parentMaritalStatusOption"
                    value={parentCaregiver.parent_marital_status}
                    onUpdate={(value: SelectValueType) => {
                      const parentMaritalStatus = Object.values(ParentMaritalStatus).find(
                        (option) => option === value,
                      );
                      if (!parentMaritalStatus) return;
                      handleUpdateField(index, 'parent_marital_status', parentMaritalStatus);
                      handleSave(index, {
                        ...parentCaregiver,
                        parent_marital_status: parentMaritalStatus,
                      });
                    }}
                    error={errors[index] ? errors[index]['parent_marital_status'] : false}
                  />
                </Grid>
              )}

              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={3} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>{`Email${
                    (!firstSavedParent && !parentCaregiver.id) ||
                    parentCaregiver.id === firstSavedParent?.id
                      ? ''
                      : ''
                  }`}</Typography>
                  <FHInputField
                    data-testid="parentEmailTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'email', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'email', parentCaregiver)}
                    error={errors[index] ? errors[index]['email'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={3} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>
                    Phone
                  </Typography>
                  <FHInputField
                    data-testid="parentPhoneTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={phoneNumberFormatter(parentCaregiver.phone ?? '')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'phone', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'phone', parentCaregiver)}
                    error={errors[index] ? errors[index]['phone'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={3} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>
                    Job
                  </Typography>
                  <FHInputField
                    data-testid="parentJobTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.job}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'job', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'job', parentCaregiver)}
                    error={errors[index] ? errors[index]['job'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={12} mt={1}>
                  <Box mb={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography display="inline" fontWeight={700}>
                      Address line one
                    </Typography>
                    {firstSavedParent && !parentCaregiver.id && (
                      <>
                        <Typography display="inline" color={FORTA_ERROR} mx={1} fontWeight={700}>
                          {`Address same as parent: ${firstSavedParent.name}`}
                        </Typography>
                        <FSwitch
                          data-testid={'fillAddressDetailsToggle'}
                          colorTheme={Themes.Primary}
                          coloredBackground
                          width={40}
                          height={20}
                          checked={fillAddressFromFirstParent}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setFillAddressFromFirstParent(e.target.checked);
                          }}
                        />
                      </>
                    )}
                  </Box>
                  <FHInputField
                    data-testid="parentAddressLineOneTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.street_address_line_one}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'street_address_line_one', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'street_address_line_one', parentCaregiver)}
                    error={errors[index] ? errors[index]['street_address_line_one'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={6}>
                  <Typography mb={0.5} mt={1} fontWeight={700}>
                    Address line two
                  </Typography>
                  <FHInputField
                    data-testid="parentAddressLineTwoTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.street_address_line_two}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'street_address_line_two', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'street_address_line_two', parentCaregiver)}
                    error={errors[index] ? errors[index]['street_address_line_two'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={2}>
                  <Typography mb={0.5} mt={1} fontWeight={700}>
                    City
                  </Typography>
                  <FHInputField
                    data-testid="parentAddressCityTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.street_address_city}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'street_address_city', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'street_address_city', parentCaregiver)}
                    error={errors[index] ? errors[index]['street_address_city'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={2} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>
                    State
                  </Typography>
                  <FHAutocomplete
                    data-testid="parentAddressStateDropDown"
                    value={parentCaregiver.street_address_state ?? ''}
                    options={usStates}
                    isOptionEqualToValue={() => true}
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    sx={{
                      '.MuiFormControl-root > .Mui-disabled': {
                        color: FORTA_BLACK,
                        backgroundColor: FORTA_BLUE.light3,
                      },
                    }}
                    coloredBackground
                    disableClearable
                    minWidth={20}
                    inputHeight={42}
                    onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: string) => {
                      handleUpdateField(index, 'street_address_state', newValue);
                      handleSave(index, {
                        ...parentCaregiver,
                        street_address_state: newValue,
                      });
                    }}
                    error={errors[index] ? errors[index]['street_address_state'] : false}
                  />
                </Grid>
              )}
              {parentCaregiver.role !== ParentCaregiverRoles['Caregiver (not parent)'] && (
                <Grid item xs={2} mt={1}>
                  <Typography mb={0.5} fontWeight={700}>
                    Zip code
                  </Typography>
                  <FHInputField
                    data-testid="parentAddressZipTextField"
                    fullWidth
                    colorTheme={Themes.Primary}
                    disabled={!isEditable}
                    inputHeight={40}
                    coloredBackground
                    value={parentCaregiver.street_address_zip}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField(index, 'street_address_zip', e.target.value)
                    }
                    onBlur={() => handleBlur(index, 'street_address_zip', parentCaregiver)}
                    error={errors[index] ? errors[index]['street_address_zip'] : false}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Divider
            sx={{ height: 1, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '3px' }}
          />
        </Box>
      ))}
      {isEditable && (
        <FHButton
          data-testid="addCareGiverButton"
          colorTheme={Themes.Primary}
          startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          sx={{ width: 'fit-content' }}
          onClick={handleAddNew}
          error={list.length === 0}
          disabled={isAdding}
        >
          Add Caregiver
        </FHButton>
      )}
    </Box>
  );
}

export default ParentsCaregivers;
